import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { eligibilitiesApi } from '../../eligibilities/eligibilitiesApi';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';
import { salaryGradesApi } from '../../plantilla/plantillaApi';
import type { SalaryGradeOption } from '../../plantilla/plantillaTypes';
import { positionsApi } from '../positionsApi';
import type { PositionListItem } from '../positionTypes';
import {
  gradeOptionsFor,
  positionDraftFrom,
  positionDraftValid,
  positionFieldIds,
  positionRequestFrom,
  qualificationRowsFrom,
  resolveStagedEligibilities,
  sampleEligibilities,
  type PositionDraft,
} from '../utils/positionDraft';
import { samplePosition } from '../utils/samplePosition';
import { PositionFields } from './PositionFields';

/**
 * Create or edit one catalog title, qualification standards included.
 *
 * The QS lives here rather than on a plantilla post because it is a property of the
 * designation: two posts drawn from the same title demand the same education, and recording
 * that twice is two rows to keep in step and one to forget. The fields themselves are
 * `<PositionFields>`, shared with the plantilla form's "new position" path.
 */
export function PositionModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PositionListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [draft, setDraft] = useState<PositionDraft>(() => positionDraftFrom(editing));

  const [grades, setGrades] = useState<SalaryGradeOption[]>([]);
  const [eligibilities, setEligibilities] = useState<EligibilityOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      salaryGradesApi.options(controller.signal),
      eligibilitiesApi.options(controller.signal),
    ])
      .then(([loadedGrades, loadedEligibilities]) => {
        setGrades(loadedGrades);
        setEligibilities(loadedEligibilities);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the pickers.', 0);

        setError(failure);
        // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, []);

  const canSave = positionDraftValid(draft) && confirmed && !saving;

  /*
    The grade is required and its only source is the published schedule, so with an empty
    schedule Autogenerate is withheld below: a button that cannot produce a saveable form is
    worse than no button.
  */
  const gradeOptions = gradeOptionsFor(grades);
  const scheduleEmpty = !loadingOptions && gradeOptions.length === 0;

  function patch(next: Partial<PositionDraft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  /**
   * Fills every required field — title, code, grade, rows of education, training and
   * experience, and the eligibility answer.
   *
   * The grade and the eligibilities are resolved against the loaded catalogs rather than
   * invented: an id the picker does not offer would leave the field looking untouched, and a
   * generated form without one would not save.
   */
  function autogenerate() {
    const sample = samplePosition();
    patch({
      title: sample.title,
      code: sample.code,
      otherExpectations: sample.otherExpectations,
      ...qualificationRowsFrom(sample),
      ...sampleEligibilities(eligibilities),
      gradeKey: gradeOptions[Math.floor(Math.random() * gradeOptions.length)].value,
    });
  }

  /**
   * Writes one staged eligibility under the catalog's own key, and keeps it in the picker's
   * options so a refused save still shows the row it now names.
   */
  async function writeEligibility(name: string) {
    const created = await eligibilitiesApi.create({ name, description: null, entailsLicense: false });
    setEligibilities((current) => [...current, { id: created.id, name: created.name, entailsLicense: false }]);
    return created.id;
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      // Eligibilities typed into a picker are written NOW, at save — see resolveStagedEligibilities.
      const resolved = await resolveStagedEligibilities(draft, writeEligibility);
      setDraft(resolved);

      const request = positionRequestFrom(resolved);

      if (editing) await positionsApi.update(editing.id, request);
      else await positionsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the position.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'position',
        // A refused ROW is named by index, so the map is built from the refusal itself.
        fieldIds: positionFieldIds('position', Object.keys(failure.fields)),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit position' : 'New position'}
      // Three document editors and two-column rows; `md` would clip both.
      size="2xl"
      id="position-modal"
      /*
        Create only — on a form over existing data it would overwrite the record's real
        values. Withheld entirely while the schedule is empty: the grade is required and its
        only source is that schedule, so the button could not produce a saveable form, and no
        button is better than one that leaves the primary action disabled.
      */
      autogenerate={
        editing === null && !scheduleEmpty
          ? { onGenerate: autogenerate, disabled: saving || loadingOptions }
          : undefined
      }
      confirm={{
        id: 'position-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="position-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="position-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <PositionFields
          idPrefix="position"
          draft={draft}
          onChange={patch}
          editing={editing}
          grades={grades}
          eligibilities={eligibilities}
          loadingOptions={loadingOptions}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
